import { localeStringComparer } from '@sembo-private/comparisons';
import type {
  HotelConstraint,
  HotelSelection,
} from '@sembo-private/constraint-builder';
import {
  deserializeConstraint,
  DestinationConstraint,
  FullTextConstraint,
  HotelCodeConstraint,
  isDestinationConstraint,
  isFullTextConstraint,
  isHotelCodeConstraint,
  isPolygonConstraint,
  isPolygonIdConstraint,
  isThemeConstraint,
  PolygonConstraint,
  PolygonIdConstraint,
  serializeConstraint,
  ThemeConstraint,
} from '@sembo-private/constraint-builder';
import { getDomain } from '@sembo-private/utils';

import {
  getSmartSearchSerializedFeatureToggles,
  isFeatureToggleActive,
} from '@libs/feature-toggle';
import { DateFormat, SemboDate } from '@libs/sembo-dates';
import type {
  AccommodationAvailabilityCriteria,
  ActivityAvailabilityCriteria,
  ActivityIngredientAdapter,
  CheapestHotelStayRequestQL,
  CheapestStayByHotelRequestQL,
  DateOnlyString,
  DestinationAvailabilityCriteria,
  HeartedHotel,
  HeartedRoom,
  HotelConstraintQL,
  HotelConstraintSelectionQL,
  HotelIngredientAdapter,
  IngredientQL,
  InputPaxQL,
  ItinerarySearchCriteria,
  MetadataRequestQL,
  OwnStayIngredientAdapter,
  Pax,
  RecipeMetaData,
  RecipeQL,
  TicketRequestQL,
  TransportAvailabilityCriteria,
  TransportIngredientAdapter,
  TransportListRequestQL,
  GraphQL,
} from '@libs/trip-planner-interfaces';
import {
  AccommodationType,
  AvailabilityOrder,
  AvailabilityOrderQL,
  BoardBasisFilter,
  IngredientType,
  IngredientTypeQL,
  InputBoardBasisQL,
  SelectedHotelTypeQL,
  SourceTypeQL,
  TransportTypeQL,
  TransportVehicleType,
  TransportVehicleTypeQL,
} from '@libs/trip-planner-interfaces';
import {
  allElementsAreDefined,
  assertValue,
  exhaustiveGuard,
  generateRandomUUID,
  isDefined,
} from '@libs/utils';

const NUMBER_OF_HOTEL_SUGGESTIONS = 15;

export function isSameStringContent(stringA: string, stringB: string): boolean {
  return (
    localeStringComparer(stringA, stringB, {
      sensitivity: 'accent',
      ignorePunctuation: true,
    }) === 0
  );
}

export function normalizeDate(date: Date): Date {
  return new SemboDate(date)
    .startOf('day')
    .add({ minutes: date.getTimezoneOffset() * -1 })
    .get();
}

export function normalizeDateOnlyString(date: undefined): undefined;
export function normalizeDateOnlyString(date: Date): DateOnlyString;
export function normalizeDateOnlyString(
  date?: Date
): DateOnlyString | undefined;
export function normalizeDateOnlyString(
  date?: Date
): DateOnlyString | undefined {
  if (date === undefined) {
    return undefined;
  }

  const dateOnlyString = new SemboDate(normalizeDate(date)).format(
    DateFormat.DateReverse
  );

  if (!isValidDateOnly(dateOnlyString)) {
    throw new Error(`Invalid dateOnly string: ${dateOnlyString}`);
  }

  return dateOnlyString;
}

function isValidDateOnly(dateString: string): dateString is DateOnlyString {
  const semboDate = new SemboDate(dateString);

  return (
    semboDate.isValidDate() &&
    semboDate.format(DateFormat.DateReverse) === dateString
  );
}

export const LOCAL_SESSION_ID = generateRandomUUID();

function setSessionId(criteria: ItinerarySearchCriteria): void {
  const sessionId = criteria.userCriteria.sessionId;
  if (!sessionId) {
    criteria.userCriteria.sessionId = LOCAL_SESSION_ID;
  }
}

const DEFAULT_MARKET_ORIGIN = 'https://www.sembo.se';

export const getMarketOrigin = (): string => {
  const marketOrigin = getDomain.isProduction
    ? window.location.origin
    : DEFAULT_MARKET_ORIGIN;

  const WHITELABEL_SUBDOMAIN = 'whitelabel';
  const GENERIC_SUBDOMAIN = 'www';
  return marketOrigin.replace(WHITELABEL_SUBDOMAIN, GENERIC_SUBDOMAIN);
};

export function getDomainMarketOrigin(): string {
  const market = getMarketOrigin();
  const endSchema = '://';
  const pathIndex = market.indexOf(endSchema) + endSchema.length;
  return market.slice(pathIndex);
}

export function getRecipeQL(criteria: ItinerarySearchCriteria): RecipeQL {
  setSessionId(criteria);

  const ingredients = criteria.userCriteria.ingredients;
  assertValue(ingredients, 'search-service-smart-search > utils > getRecipeQL');

  return {
    pax: getInputPaxQL(criteria.userCriteria.pax),
    ingredients: ingredients.map(getIngredientQL),
    market: getMarketOrigin(),
    startDateOnly: normalizeDateOnlyString(
      criteria.userCriteria.startSearchDate
    ),
    endDateOnly: normalizeDateOnlyString(criteria.userCriteria.endSearchDate),
    affiliateId: criteria.businessCriteria.affiliateId,
    featureToggles: getSmartSearchSerializedFeatureToggles(),
    skipCache: isFeatureToggleActive('skip-smart-search-cache'),
    returnEmptyIngredientResults: true,
    sessionId: criteria.userCriteria.sessionId,
    metadata: toMetaDataQL(criteria.businessCriteria.metaData),
    searchForPackageRates: criteria.businessCriteria.searchForPackageRates,
  };
}

export function getInputPaxQL(pax: Pax): InputPaxQL[] {
  return pax.map((room) => ({
    numberOfAdults: room.numberOfAdults,
    childAges: room.childAges ?? [],
  }));
}

function getIngredientQL(
  ingredient:
    | OwnStayIngredientAdapter
    | HotelIngredientAdapter
    | TransportIngredientAdapter
    | ActivityIngredientAdapter
): IngredientQL {
  switch (ingredient.ingredientType) {
    case IngredientType.OwnStay: {
      const { destination } = ingredient.constraint;
      return {
        destination,
        ingredientType: IngredientTypeQL.OwnStay,
        numberOfNights: ingredient.numberOfNights,
        subIngredients: ingredient.subIngredients.map(getIngredientQL),
      };
    }

    case IngredientType.Hotel: {
      return {
        ...ingredient.constraint,
        ingredientType: IngredientTypeQL.Hotel,
        numberOfNights: ingredient.numberOfNights,
        numberOfSuggestions: NUMBER_OF_HOTEL_SUGGESTIONS,
        heartedHotels: ingredient.heartedHotels.map(getHeartedHotelQL),
        subIngredients: ingredient.subIngredients.map(getIngredientQL),
        suggestedBy: toAvailabilityOrderQL(ingredient.suggestedBy),
        includeAlternativeResults: true,
      };
    }

    case IngredientType.Activity: {
      return {
        ingredientType: IngredientTypeQL.Activity,
        code: ingredient.code,
        offset: ingredient.offset,
        tickets: ingredient.tickets,
      };
    }

    case IngredientType.Flight: {
      return {
        transportTypes: [TransportTypeQL.Flight],
        ingredientType: IngredientTypeQL.Transport,
        fromIata: ingredient.fromPort,
        toIata: ingredient.toPort,
        flightNumbers: ingredient.transportIds,
        maxNumberOfStops: ingredient.maxNumberOfStops,
        departureTimeFilters: ingredient.departureTimeFilters,
        arrivalTimeFilters: ingredient.arrivalTimeFilters,
        maxDurationInHours: ingredient.maxDurationInHours,
        priceFilters: ingredient.priceFilters,
        carriers: ingredient.carrierFilters,
        suggestedBy: toAvailabilityOrderQL(ingredient.suggestedBy),
        includeAlternativeResults: true,
      };
    }

    case IngredientType.Ferry: {
      return {
        transportTypes: [TransportTypeQL.Ferry],
        ingredientType: IngredientTypeQL.Transport,
        fromIata: ingredient.fromPort,
        toIata: ingredient.toPort,
        flightNumbers: ingredient.transportIds,
        departureTimeFilters: ingredient.departureTimeFilters,
        arrivalTimeFilters: ingredient.arrivalTimeFilters,
        maxDurationInHours: ingredient.maxDurationInHours,
        priceFilters: ingredient.priceFilters,
        carriers: ingredient.carrierFilters,
        vehicles: ingredient.vehicles.map((vehicle) =>
          toTransportVehicleTypeQL(vehicle)
        ),
        suggestedBy: toAvailabilityOrderQL(ingredient.suggestedBy),
      };
    }

    default:
      exhaustiveGuard(ingredient, 'Ingredient adapter not handled');
  }
}

function getHeartedHotelQL(heartedHotel: HeartedHotel): GraphQL.HeartedHotelQL {
  const heartedHotelQL: GraphQL.HeartedHotelQL = {
    code: heartedHotel.code,
    isRefundable: heartedHotel.isRefundable,
    roomCodes: heartedHotel.roomCodes,
    rooms: heartedHotel.rooms?.map(getHeartedRoomQL),
  };

  if (heartedHotelQL.rooms && heartedHotelQL.rooms.length) {
    delete heartedHotelQL.roomCodes;
  }

  return heartedHotelQL;
}

function getHeartedRoomQL(heartedRoom: HeartedRoom): GraphQL.HeartedRoomQL {
  const heartedRoomQL: GraphQL.HeartedRoomQL = {
    boardBasis: heartedRoom.boardBasis,
    code: heartedRoom.code,
    roomTypeId: heartedRoom.roomTypeId,
  };

  if (isDefined(heartedRoomQL.roomTypeId)) {
    delete heartedRoomQL.code;
  }

  return heartedRoomQL;
}

export function getDestinationAvailabilityRequestQL(
  criteria: DestinationAvailabilityCriteria
): CheapestStayByHotelRequestQL {
  return {
    recipe: getRecipeQL({
      userCriteria: criteria.userCriteria.recipe,
      businessCriteria: criteria.businessCriteria,
    }),
    ingredientIndex: criteria.userCriteria.ingredientIndex,
    constraint: criteria.userCriteria.constraint,
    day: normalizeDateOnlyString(criteria.userCriteria.day),
  };
}

export function getAccommodationProductAvailabilityRequestQL(
  criteria: AccommodationAvailabilityCriteria
): CheapestHotelStayRequestQL {
  return {
    recipe: getRecipeQL({
      userCriteria: criteria.userCriteria.recipe,
      businessCriteria: criteria.businessCriteria,
    }),
    ingredientIndex: criteria.userCriteria.ingredientIndex,
    hotelCode: criteria.userCriteria.productCode,
    day: normalizeDateOnlyString(criteria.userCriteria.day),
  };
}

export function getTicketRequestQL(
  activityAvailabilityCriteria: ActivityAvailabilityCriteria
): TicketRequestQL {
  return {
    code: activityAvailabilityCriteria.userCriteria.productCode,
    fromDateOnly: normalizeDateOnlyString(
      activityAvailabilityCriteria.userCriteria.startDate
    ),
    toDateOnly: normalizeDateOnlyString(
      activityAvailabilityCriteria.userCriteria.endDate
    ),
    market: getMarketOrigin(),
    pax: getInputPaxQL(activityAvailabilityCriteria.userCriteria.pax),
    affiliateId: activityAvailabilityCriteria.businessCriteria.affiliateId,
  };
}

export function getTransportListRequestQL(
  criteria: TransportAvailabilityCriteria
): TransportListRequestQL {
  return {
    recipe: getRecipeQL({
      userCriteria: criteria.userCriteria.recipe,
      businessCriteria: criteria.businessCriteria,
    }),
    ingredientIndex: criteria.userCriteria.ingredientIndex,
    dayOnly: normalizeDateOnlyString(criteria.userCriteria.day),
    elementsInPage: criteria.userCriteria.elementsInPage,
    page: criteria.userCriteria.page,
  };
}

export function getHotelConstraint(
  constraintQL: HotelConstraintQL
): HotelConstraint & { selectedHotelTypes: AccommodationType[] } & {
  boardBasis: BoardBasisFilter[];
} {
  let selection: HotelSelection | undefined;

  if (constraintQL.destination) {
    selection = new FullTextConstraint(constraintQL.destination, true);
  }
  if (constraintQL.polygon) {
    selection = new PolygonConstraint(constraintQL.polygon);
  }
  if (constraintQL.hotelCodes) {
    selection = new HotelCodeConstraint(...constraintQL.hotelCodes);
  }
  if (constraintQL.tagId) {
    selection = new ThemeConstraint(constraintQL.tagId);
  }
  if (constraintQL.polygonId) {
    selection = new PolygonIdConstraint(constraintQL.polygonId);
  }
  if (constraintQL.destinationCode) {
    selection = new DestinationConstraint(constraintQL.destinationCode);
  }
  if (constraintQL.serializedConstraint) {
    const { selection: constraintSelection } = deserializeConstraint(
      constraintQL.serializedConstraint
    );
    selection = constraintSelection;
  }

  if (!selection) {
    throw new Error('No valid selection provided');
  }

  const {
    maxNumberOfHotels,
    hasParking,
    hasPool,
    hasSpa,
    hasGym,
    hasInternet,
    hasAirConditioning,
    hasElectricVehicleChargingStation,
    petsAreAllowed,
    isBusinessProperty,
    isFamilyProperty,
    starRating,
    freeCancellation,
    boardBasis,
    selectedHotelTypes,
  } = constraintQL;

  return {
    selection,
    maxNumberOfHotels,
    starRating,
    onlyHotelsWithParking: hasParking,
    onlyHotelsWithPool: hasPool,
    onlyHotelsWithSpa: hasSpa,
    onlyHotelsWithGym: hasGym,
    onlyHotelsWithWifi: hasInternet,
    onlyHotelsWithAir: hasAirConditioning,
    onlyHotelsWithVehicleChargingStation: hasElectricVehicleChargingStation,
    onlyHotelsWithPetsAllowed: petsAreAllowed,
    onlyHotelsWithBusinessProperty: isBusinessProperty,
    onlyHotelsWithFamilyProperty: isFamilyProperty,
    onlyHotelsWithFreeCancellation: freeCancellation,
    boardBasis: boardBasis.map(toSelectedBoardBasisFilter),
    selectedHotelTypes: selectedHotelTypes.map(toAccommodationType),
  };
}

export function getHotelConstraintSelectionQL(
  constraint: HotelConstraint
): HotelConstraintSelectionQL {
  const { selection } = constraint;

  if (isFullTextConstraint(selection)) {
    return { destination: selection.freeText };
  }

  if (isPolygonConstraint(selection)) {
    return { polygon: selection.coordinates };
  }

  if (isHotelCodeConstraint(selection)) {
    return {
      hotelCodes: selection.hotelCodes,
    };
  }

  if (isPolygonIdConstraint(selection)) {
    return {
      polygonId: Number(selection.serializeValue()),
    };
  }

  if (isThemeConstraint(selection)) {
    return {
      tagId: Number(selection.serializeValue()),
    };
  }

  if (isDestinationConstraint(selection)) {
    return {
      destinationCode: Number(selection.serializeValue()),
    };
  }

  return {
    serializedConstraint: serializeConstraint(constraint),
  };
}

export function getHotelConstraintQL(
  constraint: HotelConstraint & {
    selectedHotelTypes: AccommodationType[];
    selectedBoardBasis: BoardBasisFilter[];
  }
): HotelConstraintQL {
  const {
    maxNumberOfHotels,
    onlyHotelsWithParking,
    onlyHotelsWithPetsAllowed,
    onlyHotelsWithBusinessProperty,
    onlyHotelsWithFamilyProperty,
    onlyHotelsWithFreeCancellation,
    onlyHotelsWithPool,
    onlyHotelsWithSpa,
    onlyHotelsWithGym,
    onlyHotelsWithWifi,
    onlyHotelsWithAir,
    onlyHotelsWithVehicleChargingStation,
    starRating,
    selectedHotelTypes,
    selectedBoardBasis,
    hotelName,
  } = constraint;

  return {
    ...getHotelConstraintSelectionQL(constraint),
    selectedHotelTypes: selectedHotelTypes.map(toSelectedHotelType),
    maxNumberOfHotels,
    starRating,
    hasParking: onlyHotelsWithParking,
    hasPool: onlyHotelsWithPool,
    hasSpa: onlyHotelsWithSpa,
    hasGym: onlyHotelsWithGym,
    hasInternet: onlyHotelsWithWifi,
    hasElectricVehicleChargingStation: onlyHotelsWithVehicleChargingStation,
    hasAirConditioning: onlyHotelsWithAir,
    petsAreAllowed: onlyHotelsWithPetsAllowed,
    isBusinessProperty: onlyHotelsWithBusinessProperty,
    isFamilyProperty: onlyHotelsWithFamilyProperty,
    freeCancellation: onlyHotelsWithFreeCancellation,
    boardBasis: selectedBoardBasis?.map(toSelectedInputBoardBasisQL),
    hotelName,
  };
}

function toAccommodationType(
  selectedHotelTypeQL: SelectedHotelTypeQL
): AccommodationType {
  // TODO: isSameStringContent is used to provide support for multi case values for the same business entities
  // coming from SmartSearch and URLs constructed at different points in time
  return mapEnumValue(
    SelectedHotelTypeQL,
    AccommodationType,
    selectedHotelTypeQL,
    (value) => isSameStringContent(value, selectedHotelTypeQL)
  );
}

export function toSelectedHotelType(
  accommodationType: AccommodationType
): SelectedHotelTypeQL {
  // TODO: isSameStringContent is used to provide support for multi case values for the same business entities
  // coming from SmartSearch and URLs constructed at different points in time
  return mapEnumValue(
    AccommodationType,
    SelectedHotelTypeQL,
    accommodationType,
    (value) => isSameStringContent(value, accommodationType)
  );
}

function toAvailabilityOrderQL(
  availabilityOrder: AvailabilityOrder = AvailabilityOrder.None
): AvailabilityOrderQL {
  switch (availabilityOrder) {
    case AvailabilityOrder.None:
      return AvailabilityOrderQL.None;

    case AvailabilityOrder.Cheapest:
      return AvailabilityOrderQL.Cheapest;

    case AvailabilityOrder.Popular:
      return AvailabilityOrderQL.Popular;

    default:
      exhaustiveGuard(availabilityOrder, 'Unhandled availability order');
  }
}

function toMetaDataQL(metaData?: RecipeMetaData): MetadataRequestQL {
  return {
    sourceType: SourceTypeQL.Planner,
    generatedFromAI: metaData?.generatedFromAI,
  };
}

function toTransportVehicleTypeQL(
  vehicle: TransportVehicleType
): TransportVehicleTypeQL {
  return mapEnumValue(TransportVehicleType, TransportVehicleTypeQL, vehicle);
}

export function toSelectedInputBoardBasisQL(
  boardBasis: BoardBasisFilter
): InputBoardBasisQL {
  return mapEnumValue(BoardBasisFilter, InputBoardBasisQL, boardBasis);
}
export function toSelectedBoardBasisFilter(
  boardBasis: InputBoardBasisQL
): BoardBasisFilter {
  return mapEnumValue(InputBoardBasisQL, BoardBasisFilter, boardBasis);
}

export function mapEnumValue<T, U>(
  enum1: Record<string, T>,
  enum2: Record<string, U>,
  value: T,
  predicate: (iteratee: T) => boolean = (enum1Value): boolean =>
    enum1Value === value
): U {
  const enumKey = Object.keys(enum1)[Object.values(enum1).findIndex(predicate)];
  assertValue(
    enumKey,
    `search-service-smart-search > utils.ts > mapEnumValue > enumKey > ${value} > ${Object.values(
      enum1
    ).join(', ')}`
  );

  const enumValue = enum2[enumKey];
  assertValue(
    enumValue,
    'search-service-smart-search > utils.ts > mapEnumValue > enumValue'
  );

  return enumValue;
}

// TODO: Remove SmartSearch assertions when Insights confirm their Schema consistency
export function assertSmartSearchListOutput<T>(
  output: (T | null)[] | null | undefined
): asserts output is T[] {
  if (!isDefined(output) || !allElementsAreDefined(output)) {
    throw new Error('Not valid SmartSearch list output');
  }
}
